import React, { useEffect, useState } from 'react';
import Icon from '../shared/Icon';
import DocumentReviewQueue from './DocumentReviewQueue';
import DocumentReviewFields from './DocumentReviewFields';
import DocumentReviewPhases from './DocumentReviewPhases';
import DocumentReviewFooter from './DocumentReviewFooter';
import DocumentViewerOverlay from './DocumentViewerOverlay';
import { useEscapeKey } from '../shared/useEscapeKey';
import { commitNotice } from '../../../utils/documentReviewNotice';
import './DocumentReviewPanel.css';

/** Phases where the body shows a spinner or an exit, never the queue. */
const NON_WORKING_PHASES = ['loading', 'submitting', 'failed', 'completed'];

/**
 * Document field review (HITL) modal.
 *
 * Deliberately the same shell as CsvReviewModal — fixed backdrop, centred card,
 * bordered header with a close icon, scrolling body, right-aligned sticky
 * footer, spinner phases — so the two review surfaces read as one feature to a
 * reviewer. Geometry and colours below are copied from CsvReviewModal.css.
 *
 * One document at a time with ALL of its flagged fields stacked. The reviewer
 * settles every field, then "Save & continue" commits that document in a single
 * call and moves on — the release unit is the document, so each one is released
 * as soon as it is done rather than waiting for the whole dataset.
 */
// PRD-1359: the dataset context menu the feedback doc asks for in both this
// header and the document view's. Passed in as a node rather than assembled
// here — the actions (share, edit, pause, delete) belong to the Data Sources
// page that owns them, and this component has no business knowing about them.
const DocumentReviewPanel = ({ vm, onClose, onShowNotification, onBack, menu }) => {
  const [viewer, setViewer] = useState(null); // { page, fieldLabel, entityId, value } | null
  // Set by "Correct" in the document view: the field whose input should take
  // focus once the fields step is back on screen.
  const [focusEntityId, setFocusEntityId] = useState(null);
  const {
    phase, documents, activeIndex, activeDocument, overallProgress,
    invalidFields, error, datasetName, committedCount, nextCursor, loadingMore,
  } = vm;

  const openSource = (page, fieldLabel, flag) => setViewer({
    page,
    fieldLabel,
    entityId: flag.entity_id,
    value: flag.extracted_value ?? '',
  });

  // The fields step is unmounted while the viewer is up, so the input only
  // exists again after the commit that closed it — hence an effect, not a
  // focus() call in the handler.
  useEffect(() => {
    if (viewer || !focusEntityId) return;
    document.getElementById(`docreview-input-${focusEntityId}`)?.focus();
    setFocusEntityId(null);
  }, [viewer, focusEntityId]);

  // One Escape target, because there is one modal: while the document view is
  // up Escape steps back to the fields rather than discarding the whole review.
  useEscapeKey(true, viewer ? () => setViewer(null) : onClose);

  // onShowNotification is an optional prop throughout this codebase, so never
  // call it bare — an undefined prop would throw mid-handler, after the commit
  // has already succeeded server-side.
  const notify = (message, type) => {
    if (onShowNotification) onShowNotification(message, type);
  };

  // The document view's three decisions. Each one returns to Review fields.
  // Verify stages through stageValue so the model classifies it (an unedited
  // extracted value is `confirm`); Can't verify is `skip`; Correct stages
  // nothing — it hands the reviewer the input.
  const verifyFromViewer = () => {
    if (vm.stageValue(activeDocument.document_id, viewer.entityId, viewer.value)) setViewer(null);
  };
  const correctFromViewer = () => {
    setFocusEntityId(viewer.entityId);
    setViewer(null);
  };
  const cantVerifyFromViewer = () => {
    vm.stageResolution(activeDocument.document_id, viewer.entityId, 'skip');
    setViewer(null);
  };

  const handleCommit = async () => {
    const doc = activeDocument;
    if (!doc) return;
    const outcome = await vm.commitDocument(doc.document_id);
    const notice = commitNotice(outcome, doc.file_name);
    if (notice) notify(notice.message, notice.type);
  };

  const isReviewing = phase === 'reviewing';
  const doc = activeDocument;
  const hasWork = documents.length > 0 && !!doc;
  const showWork = hasWork && !NON_WORKING_PHASES.includes(phase);

  const complete = hasWork ? vm.isDocumentComplete(doc.document_id) : false;
  // No flags => no footer, so a zero-flag document cannot be submitted.
  //
  // This looks like a gap and was raised as one: the pending-review list keeps
  // zero-flag documents deliberately, partly so a reviewer can release one that
  // got stuck after its flags were resolved, and `POST .../review/resolve`
  // does accept `{ resolutions: [] }` and returns released: true.
  //
  // DO NOT open it up on that basis. Backend confirmed a document parks with no
  // flags for two independent reasons, and the list response cannot tell them
  // apart yet: stuck mid-release (releasing is right) OR a scan too unreadable
  // to measure (releasing is NOT — resolve does not re-check readability, so an
  // empty submit would push an unreviewed document to READY and into
  // knowledge-search). Until the API carries `park_reason` and refuses an empty
  // release for `unreadable_scan`, this gate is the only thing preventing that,
  // and the safe failure (a stuck document needs support) is much cheaper than
  // the unsafe one.
  const measurable = hasWork && (doc.flags || []).length > 0;

  return (
    <>
      {/* Same target as Escape, deliberately. These are two ways of saying
          "dismiss the top layer", and they used to disagree: in the document
          view Escape stepped back to the fields while an outside click closed
          the whole review. One gesture recoverable, its twin destructive. */}
      <div
        className="docreview-backdrop"
        onClick={viewer ? () => setViewer(null) : onClose}
      />
      <div className="docreview-modal" role="dialog" aria-modal="true" aria-label="Document field review">
        {/* PRD-1359: the document view is the third step of THIS modal, not a
            modal on top of it — one backdrop, one Escape target, and a
            breadcrumb that walks back up. Swapped in place of the review
            content rather than layered over it. */}
        {viewer && doc ? (
          <DocumentViewerOverlay
            datasetId={vm.datasetId}
            fileId={doc.file_id}
            fileName={doc.file_name}
            fieldLabel={viewer.fieldLabel}
            value={viewer.value}
            page={viewer.page}
            datasetName={datasetName}
            menu={menu}
            onBack={() => setViewer(null)}
            onBackToDataset={onBack && (() => { setViewer(null); onBack(); })}
            onClose={onClose}
            onVerify={verifyFromViewer}
            onCorrect={correctFromViewer}
            onCantVerify={cantVerifyFromViewer}
          />
        ) : (
        <>
        {/* Figma 1018-2835: the modal is titled with the DATASET, and says where
            you are via the breadcrumb and the "Review fields" heading below —
            not by stuffing both into one title string. */}
        <div className="docreview-header">
          <div className="docreview-titlebar">
            {onBack && (
              <button
                type="button"
                className="docreview-back"
                aria-label="Back to dataset details"
                onClick={onBack}
              >
                <Icon name="chevron_left" />
              </button>
            )}
            <h2 className="docreview-title">{datasetName || 'Review fields'}</h2>
            {menu}
            <button className="docreview-close" aria-label="Close" onClick={onClose}>
              <Icon name="close" />
            </button>
          </div>
          {/* Back and the breadcrumb's first crumb are the same action; the
              breadcrumb is the label for it, so it is only a link when there is
              somewhere to go back to. */}
          <nav className="docreview-breadcrumbs" aria-label="Breadcrumb">
            {onBack ? (
              <button type="button" className="resui-link" onClick={onBack}>
                Dataset details
              </button>
            ) : (
              <span className="docreview-crumb">Dataset details</span>
            )}
            <Icon name="chevron_right" className="docreview-crumb-sep" size={16} />
            <span className="docreview-crumb" aria-current="page">Review fields</span>
          </nav>
        </div>

        {error && isReviewing && (
          <div className="docreview-error-banner">
            <Icon name="warning" />
            <span>{error}</span>
          </div>
        )}

        <div className="docreview-modal-body">
          {showWork ? (
            <>
              <h3 className="docreview-section-title">Review fields</h3>
              {/* Figma 1018-2835 places the `Banner` component here (node
                  1018:2847) in its Info variant. `.docreview-banner` now carries
                  only the spacing this layout needs — same split ReviewRequiredBanner
                  uses for its alert variant. */}
              <div className="resui-banner resui-banner--info docreview-banner">
                <Icon name="info_outline" />
                <span className="resui-banner-text">
                  Extraction wasn’t confident about the fields below. Verify the value it read,
                  correct it, or mark it as not present — then save the document to release it.
                </span>
              </div>

              <div className="docreview-body-split">
                <DocumentReviewQueue
                  documents={documents}
                  activeIndex={activeIndex}
                  nextCursor={nextCursor}
                  loadingMore={loadingMore}
                  onSelect={vm.setActiveIndex}
                  onLoadMore={() => vm.loadMoreDocuments()}
                />
                <DocumentReviewFields
                  doc={doc}
                  invalidFields={invalidFields}
                  getStagedResolution={vm.getStagedResolution}
                  onStage={vm.stageResolution}
                  onStageValue={vm.stageValue}
                  onUnstage={vm.unstageResolution}
                  onOpenSource={openSource}
                />
              </div>
            </>
          ) : (
            <DocumentReviewPhases
              phase={phase}
              error={error}
              committedCount={committedCount}
              onClose={onClose}
            />
          )}
        </div>

        {/* Cancel returns to Dataset details, per design. `onBack` is the same
            handler the breadcrumb uses; it is optional, so fall back to closing
            outright when the panel was opened without one. */}
        {/* The count is the WHOLE queue, not the active document — the Save
            gate below stays per document, which is the release unit. */}
        {isReviewing && measurable && (
          <DocumentReviewFooter
            settled={overallProgress.settled}
            total={overallProgress.total}
            isLast={activeIndex === documents.length - 1}
            complete={complete}
            onCancel={onBack || onClose}
            onCommit={handleCommit}
          />
        )}
        </>
        )}
      </div>
    </>
  );
};

export default DocumentReviewPanel;
